import type {
  Activity,
  CompoundedPackage,
  CompoundedProgress,
  SimpleProgress,
  StudentProgress
} from '../models/Activity'
import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import activityApi from '../api/activityApi'
import { useAuthStore } from './auth'
import { useDebounce } from '../composables/debounce'
import useGroupsStore from './groups'
import { useI18n } from '../composables/useI18n'
import useArrayUtils from '~/utils/arrayUtils'

const { t } = useI18n()
const { debounce } = useDebounce()
const {
  getSimpleProgress,
  getCompoundProgress,
} = activityApi()

export const useActivityStore = defineStore('activity',  () => {
  const authStore = useAuthStore()
  const {
    isTeacher,
    isStudent,
    userId,
  } = storeToRefs(authStore)

  /* State properties */
  const activities = ref<Activity[]>([])
  const wantsActivity = ref<number[]>([])
  const hasActivity = ref<number[]>([])
  const missingActivity = ref<number[]>([])
  const fetchDebounce = ref(500)

  // Any locations that have not been requested yet
  const diff = computed(() => {
    return wantsActivity.value.filter(
      locationId => !hasActivity.value.includes(locationId) && !missingActivity.value.includes(locationId),
    )
  })

  // Load activity for locations when requested
  watch(diff, () => {
    if (diff.value.length === 0) {
      return
    }
    loadActivities(diff.value)
  })

  // Reset store when group-members is changed
  const groupStore = useGroupsStore()
  const { activeGroupStudentIds } = storeToRefs(groupStore)
  watch(activeGroupStudentIds, (current, prev) => {
      if(current.sort().toString() !== prev.sort().toString() || current.length === 0) {
        resetActivityStore()
      }
    },
  )

  // Get activity for location if it exists
  const getActivityByLocationId = (locationId: number): null | Activity => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    if (!locationId) return null

    if (locationHasActivity(locationId)) {
      const activity: Activity | null = findActivityByLocationId(locationId)
      if(activity === null) {
        return null
      }
      return activity
    }

    if (locationWantsActivity(locationId)) {
      return null
    }
    addWantsActivity(locationId)

    return null
  }

  const createStudentListFromCompounded = (compoundedProgress: CompoundedProgress): StudentProgress[] => {
    const { activeGroupStudents } = useGroupsStore()

    const studentList: StudentProgress[] = activeGroupStudents.map((student) => {
      const activity = compoundedProgress.progress.find((progress) => progress._id === student.userId)
      const interaction = activity?.packages[0]?.lastInteraction || undefined
      const status = compoundedProgress.progress.find((progress) => progress._id === student.userId)
        ? t('activity.status.visited')
        : t('activity.status.notStarted')

      return {
        user: student,
        status: status,
        lastInteraction: interaction ? DateTime.fromISO(interaction).toLocaleString({ month: 'long', day: '2-digit' }) : '',
        read: activity?.packages[0]?.read || 0,
        sectionCount: activity?.packages[0]?.sectionCount || 0,
        progress: activity?.packages[0]?.progress ? Math.round((activity?.packages[0]?.progress * 100)) : 0,
        formWriterId: activity?.packages[0]?.formWriterId || '',
        locationId: parseInt(activity?.packages[0].pageId || '0'),
      }
    })

    studentList.sort((a: StudentProgress, b: StudentProgress) => {
      const nameA = a.user.fullName.toUpperCase()
      const NameB = b.user.fullName.toUpperCase()
      return (nameA < NameB) ? -1 : (nameA > NameB) ? 1 : 0
    })

    return studentList || []
  }

  const createActivitiesFromCompounded = (compoundedProgress: CompoundedProgress): Activity[] => {
    const packages: CompoundedPackage[] = compoundedProgress.progress[0]?.packages

    const studentActivities: Activity[] = packages?.map((pkg: CompoundedPackage) => {
        return {
          title: '',
          locationId: parseInt(pkg.pageId || '0'),
          totalUsers: undefined,
          visitedUsers: undefined,
          lastInteraction: pkg.lastInteraction,
          progress: Math.round((pkg.progress * 100)),
          read: pkg.read,
          sectionCount: pkg.sectionCount,
        }
    })

    return studentActivities || []
  }

  const loadCompoundProgressByLocations = async (locationIds: number[]): Promise<void> => {
    if (!locationIds.length) return
    if (!Number.isInteger(locationIds[0])) {
      throw new Error(`Expected locationIds to be a number, but got ${typeof(locationIds[0])}`)
    }
    if (!userId.value || locationIds.length === 0) return
    const { unique } = useArrayUtils()
    locationIds = locationIds.filter(unique)

    let compoundedProgress: CompoundedProgress
    try {
      compoundedProgress = await getCompoundProgress([userId.value], locationIds)
    } catch (e) {
      console.error(`Could not load compounded activity at ${locationIds} for user ${userId.value}`)
      throw e
    }
    const studentActivities = createActivitiesFromCompounded(compoundedProgress)
    const locationsWithActivity = studentActivities.map((activity) => activity.locationId)
    const locationsWithoutActivity = locationIds.filter((id) => !locationsWithActivity.includes(id))

    addActivities(studentActivities)
    addHasActivities(locationsWithActivity)
    addMissingActivities(locationsWithoutActivity)
  }

  const loadCompoundProgressByLocationsAsList = async (locationIds: number[]): Promise<StudentProgress[]> => {
    if (!locationIds.length) return []
    if (!Number.isInteger(locationIds[0])) {
      throw new Error(`Expected locationIds to be a number, but got ${typeof(locationIds[0])}`)
    }
    const { activeGroupStudentIds } = useGroupsStore()

    if(locationIds.length === 0 || activeGroupStudentIds.length === 0) {
      return []
    }

    let compoundedProgress: CompoundedProgress | undefined = undefined
    try {
      compoundedProgress = await getCompoundProgress(activeGroupStudentIds, locationIds)
    } catch (e) {
      console.error(`Could not load compounded activity at ${locationIds} for users ${activeGroupStudentIds}`)
      throw e
    }

    return createStudentListFromCompounded(compoundedProgress)
  }

  const loadActivities = debounce(async (locationIds: number[]): Promise<void> => {
    if (!locationIds.length) return
    if (!Number.isInteger(locationIds[0])) {
      throw new Error(`Expected locationIds to be a number, but got ${typeof(locationIds[0])}`)
    }
    if (isStudent.value) {
      await loadCompoundProgressByLocations(locationIds)
    }
    if (isTeacher.value) {
      await loadSimpleActivities(locationIds)
    }
  }, fetchDebounce.value)

  const loadSimpleActivities = async (locationIds: number[]): Promise<void> => {
    if (!locationIds.length) return
    if (!Number.isInteger(locationIds[0])) {
      throw new Error(`Expected locationIds to be a number, but got ${typeof(locationIds[0])}`)
    }
    const { activeGroupStudentIds } = useGroupsStore()

    if(locationIds.length === 0 || activeGroupStudentIds.length === 0) {
      return
    }

    let simpleProgress: SimpleProgress | undefined = undefined
    try {
      simpleProgress = await getSimpleProgress(activeGroupStudentIds, locationIds)
    } catch (e) {
      console.error(`Could not load simple activity ${locationIds} for group ${activeGroupStudentIds}`)
      throw e
    }

    const simpleActivities = simpleProgress.progress.map((simpleActivity): Activity => {
      return {
        title: simpleActivity.title,
        locationId: parseInt(simpleActivity.pageId || '0'),
        totalUsers: simpleProgress?.totalNumUsers,
        visitedUsers: simpleActivity.noOfUsers,
        lastInteraction: undefined,
        progress: undefined,
        read: undefined,
        sectionCount: undefined,
      }
    })

    const hasActivity = simpleActivities.map((activity) => activity.locationId)
    const missingActivity = [...(new Set(locationIds.filter((id) => !hasActivity.includes(id))))]

    addActivities(simpleActivities)
    addHasActivities(hasActivity)
    addMissingActivities(missingActivity)
  }

  const addMissingActivities = (locationIds: number[]): void => {
    if (!locationIds.length) return
    if (!Number.isInteger(locationIds[0])) {
      throw new Error(`Expected locationIds to be a number, but got ${typeof(locationIds[0])}`)
    }
    missingActivity.value = [...(new Set([...missingActivity.value, ...locationIds]))]
  }

  const findActivityByLocationId = (locationId: number): Activity | null => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    return activities.value.find((activity) => activity.locationId === locationId) || null
  }

  const addWantsActivity = (locationId: number): void => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    if (!Number.isInteger(locationId)) throw new Error('that is not a number, but something else')
    if(!wantsActivity.value.includes(locationId)) {
      wantsActivity.value = [...(new Set([...wantsActivity.value, locationId]))]
    }
  }

  const locationHasActivity = (locationId: number): boolean => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    return hasActivity.value.includes(locationId)
  }

  const locationWantsActivity = (locationId: number): boolean => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    return wantsActivity.value.includes(locationId)
  }

  const addHasActivities = (locationIds: number[]): void => {
    if (!locationIds.length) return
    if (!Number.isInteger(locationIds[0])) {
      throw new Error(`Expected locationIds to be a number, but got ${typeof(locationIds[0])}`)
    }
    hasActivity.value = [...(new Set([...hasActivity.value, ...locationIds]))]
  }

  const addActivity = (newActivity: Activity): void => {
    activities.value = [...(new Set([...activities.value, newActivity]))]
  }

  const addActivities = (newActivities: Activity[]): void => {
    newActivities.forEach((activity) => {
      if(locationHasActivity(activity.locationId)) {
        replaceActivity(activity)
      } else {
        addActivity(activity)
      }
    })
  }

  const removeActivity = (locationId: number): void => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    activities.value = activities.value.filter((activity) => activity.locationId !== locationId)
  }

  const removeWantsActivity = (locationId: number): void => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    wantsActivity.value = wantsActivity.value.filter((id) => id !== locationId)
  }

  const removeHasActivity = (locationId: number): void => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    hasActivity.value = hasActivity.value.filter((id) => id !== locationId)
  }

  const removeMissingActivity = (locationId: number): void => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    missingActivity.value = missingActivity.value.filter((id) => id !== locationId)
  }

  const replaceActivity = (activity: Activity): void => {
    removeActivity(activity.locationId)
    addActivity(activity)
  }

  const resetActivityStore = () => {
    activities.value = []
    wantsActivity.value = []
    hasActivity.value = []
    missingActivity.value = []
  }

  const clearActivityByLocationId = async (locationId: number): Promise<void> => {
    if (!Number.isInteger(locationId)) {
      throw new Error(`Expected locationId to be a number, but got ${typeof(locationId)}`)
    }
    removeActivity(locationId)
    removeWantsActivity(locationId)
    removeHasActivity(locationId)
    removeMissingActivity(locationId)
  }

  return {
    activities,
    wantsActivity,
    hasActivity,
    missingActivity,
    fetchDebounce,
    loadCompoundProgressByLocationsAsList,
    getActivityByLocationId,
    resetActivityStore,
    clearActivityByLocationId,
  }
})
